import * as PropTypes from 'prop-types';

import {InaccessibleInfo} from 'auth/RequestAccess';
import ModelTypeConstants, {BioentityType} from 'constants/ModelTypeConstants';
import {Side} from 'constants/MolbioConstants';
import {TagSchemaSummary} from 'constants/RegistryConstants';
import {BatchSchema, StorableSummary} from 'constants/SampleConstants';
import {BioentitySummary, SchemaFieldDataType} from 'folder-items/FileConstants';
import {AntibodyDomainType, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec, LookupSortType} from 'lookup-config/LookupConfigModalConstants';
import {
  BulkAssemblyFragmentBin,
  BulkAssemblyMethod,
  SpecifiedFragmentInfo,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {AddStructuredTableRowsUsingAIResult} from 'notebook/ai-tools/flexible-data-entry/addStructuredTableRowsUsingAI';
import {ClientId, FromServerId, NLOId, ToServerId} from 'notebook/ELNConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {OptionCellDropdownOption} from 'notebook/plain-tables/selectors/OptionCellDropdown';
import {AddToTableOptions} from 'notebook/sample-propagation/util/addSamplesToTable';
import {ColumnDefinition, ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {LiveCellValue} from 'notebook/structured-tables/LiveCellValues';
import {OutsideNotebookContext} from 'notebook/structured-tables/StructuredTableOutsideNotebookConstants';
import {EphemeralObjectSpec} from 'notebook/structured-tables/util/EphemeralObjectSpec';
import {GetFooterInformationParams} from 'notebook/structured-tables/util/getFooterInformation';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {EvaluatedCell, EvaluatedCellError, EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {
  Cell,
  cell,
  CellIndex,
  Cells,
  CustomEvalFnAndType,
  MultiSelectionBox,
  Row,
  RowData,
  SelectionRange,
  Table,
  TableColumnType,
  TableData,
  TableIndicatorStatus,
  TableOpDiff,
  TableOpFunction,
  TableOpOptions,
  TableReorderLocks,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {Note, NotePart, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {ProcedureStepConfigEditorContextValue} from 'procedures/design/step-config/ProcedureStepConfigEditorContext';
import {ProcedureSchemaFieldTypeContext} from 'procedures/tables/ProcedureTableConstants';
import {ProcessTaskSchema} from 'processes/ProcessConstants';
import {SampleLookupInfoForCreateRequest} from 'requests/creation/CreateRequestFromSamplesButton';
import {FrozenEntityChipData} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {
  PlateMaterialInput,
  ReagentAssignmentGroup,
  TestOrderInfoById,
} from 'samples/plate-maps/PlateMapConstants';
import {Position} from 'samples/SampleUtil';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {Schema, SchemaWithDescription} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';

import {ProcessTaskStatusLifecycleGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskStatusLifecycleGraphQL';
import {ActionFunctionContext} from './action-functions/ActionFunctionContext';
import {BasesColumnConfig} from './bases-columns/BasesColumnConstants';
import {CustomColumnConfig} from './custom-columns/CustomColumnConstants';

// the kind needs to match the StructuredTable.Kind enum in
// benchling/notebook/structured_tables/models/structured_table.py
// and should also stay in sync with
// browser-tests/lib/notebook/regions/entries/StructuredTableView.ts
export enum StructuredTableKind {
  MIXTURE_METADATA = 'MIXTURE_METADATA',
  MIXTURE_INGREDIENTS = 'MIXTURE_INGREDIENTS',
  INVENTORY = 'INVENTORY',
  PLATE_CREATION = 'PLATE_CREATION',
  BOX_CREATION = 'BOX_CREATION',
  RUN_OUTPUT_SAMPLES = 'RUN_OUTPUT_SAMPLES',
  REGISTRATION = 'REGISTRATION',
  RESULTS = 'RESULTS',
  TRANSFER = 'TRANSFER',
  PROCESS_OUTPUT = 'PROCESS_OUTPUT',
  PROCESS_TASK = 'PROCESS_TASK',
  LOOKUP = 'LOOKUP',
  PARAMETER = 'PARAMETER',
  STOICHIOMETRY = 'STOICHIOMETRY',

  // Fill-plate transfer tables are never used in the notebook, so they aren't persisted
  FILL_PLATE_TRANSFER_SUMMARY_VIEW = 'FILL_PLATE_TRANSFER_SUMMARY_VIEW',
  FILL_PLATE_TRANSFER_WELL_VIEW = 'FILL_PLATE_TRANSFER_WELL_VIEW',
  FILL_PLATE_TRANSFER_PLATE_RECORD = 'FILL_PLATE_TRANSFER_PLATE_RECORD',

  // Bulk assembly tables are never used in a notebook context, so do not need to be specified on the backend.
  BULK_ASSEMBLY_FRAGMENTS = 'BULK_ASSEMBLY_FRAGMENTS',
  BULK_ASSEMBLY_CONSTRUCTS = 'BULK_ASSEMBLY_CONSTRUCTS',
  BULK_ASSEMBLY_PRIMERS = 'BULK_ASSEMBLY_PRIMERS',

  // These Procedures table types are for configuration only. They are not persisted or rendered
  // in notebook entries or documents.
  PROCEDURE_MATERIAL_INPUTS = 'PROCEDURE_MATERIAL_INPUTS',
  PROCEDURE_MATERIAL_OUTPUTS = 'PROCEDURE_MATERIAL_OUTPUTS',
  PROCEDURE_DATA_PARAMETERS_CONFIG = 'PROCEDURE_DATA_PARAMETERS_CONFIG',
  PROCEDURE_EQUIPMENT = 'PROCEDURE_EQUIPMENT',
  TEST_DEFINITIONS = 'TEST_DEFINITIONS',

  APP_CANVAS = 'APP_CANVAS',
}

export const STRUCTURED_TABLE_KINDS_TO_SHOW_CSV_METADATA = [
  StructuredTableKind.RESULTS,
  StructuredTableKind.PROCESS_TASK,
  StructuredTableKind.PROCESS_OUTPUT,
];

export const STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE: ReadonlyArray<StructuredTableKind | null> = [
  StructuredTableKind.MIXTURE_INGREDIENTS,
  StructuredTableKind.MIXTURE_METADATA,
];

export const ALL_PARAMETER_STRUCTURED_TABLE_KINDS = [
  StructuredTableKind.PARAMETER,
  StructuredTableKind.PROCEDURE_MATERIAL_INPUTS,
  StructuredTableKind.PROCEDURE_MATERIAL_OUTPUTS,
  StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG,
  StructuredTableKind.PROCEDURE_EQUIPMENT,
];

// This is the row interface used in TransferTables and RegistrationTables.  Unlike the
// StructuredTableRow below, it doesn't have a rowId or the ability to add metadata to
// extend the interface.
// TODO(BNCH-9195) Consolidate this with StructuredTableRow
export interface LegacyStructuredTableRow {
  isFinalized: boolean;
  cells: Array<Cell>;
}

export const LEGACY_STRUCTURED_TABLE_ROW_SHAPE = PropTypes.shape({
  isFinalized: PropTypes.bool.isRequired,
  cells: PropTypes.arrayOf(cell.isRequired).isRequired,
});

export const DEFAULT_COLUMN_WIDTH = 118;

export type BaseStructuredTableColumn<TIds, TFrontEndMetadata = unknown> = {
  provenanceUuid: string;
  columnType: TableColumnType;
  // TODO(TypeScript): add stricter type validation
  defaultValue: Cell;
  name: string;
  width: number;

  isReadOnly: boolean;

  /**
   * The isProtected parameter indicates that a user locked this column in a template to prevent user edits
   */
  isProtected: boolean;
  isRequired: boolean;
  isArchived: boolean;
  isReported: boolean;
  columnGroup?: ColumnGroup | null;

  schemaFieldApiId?: string | null;
  tagSchemaFieldId?: number | null;

  // Columns can set up their own metadata as they see fit. This value is not persisted. Install your
  // metadata in the ColumnDefinition in `toColumnDefinition()`
  frontEndMetadata?: TFrontEndMetadata;
  isSingleInput?: boolean | null;
} & TIds;

export interface SchemaFieldColumnUpdate {
  isRequired: boolean;
  isArchived: boolean;
  schemaFieldDisplayName: string;
  schemaFieldDescription: string | undefined;
  schemaFieldDecimalPrecision: number | null;
  schemaFieldApiId: string;
  schemaFieldType: FieldType;
  schemaFieldIsComputed: boolean;
  schemaFieldIsConvertedFromLink: boolean;
}

export interface BaseActionFunctionParameters<TTableMetadata, TRowMetadata> {
  entryOrTemplateId: string | null;
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  structuredTableAdapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>;
  computedTable: Array<Array<EvaluatedCell>>;
  table?: Table;
  liveRowMetadataByRow?: Array<LiveRowMetadata>;
  tableStatus?: TableIndicatorStatus;
  isCollapsed?: boolean;

  /**
   * The locked parameter is set if the entry is currently locked.  This could be
   * because we're viewing an older version or because the user doesn't have permission
   * to edit the entry.  We may want to disable certain action functions when this
   * is the case as we don't want to allow the user to perform the action.
   */
  locked: boolean;

  /**
   * The isProtected parameter is set if the table is currently protected.  This occurs when
   * the entry is created from a template that is fill-in only, and thus several editability
   * actions are restricted such as editing code blocks, text, tables, file attachments, and
   * content blocks.
   */
  isProtected?: boolean;

  /**
   * The updateHandler exposes a way to update the content of the StructuredTable.  This
   * updateHandler can be used when the Action Function doesn't require modifying any other
   * resources other than the table in the entry.  For other updates which require updating
   * resources outside of the table, you should use the back-end Action Function and not this
   * updateHandler.
   *
   * This handler also allows the ActionFunction to specify if the
   * `lastSyncedUpstreamValues` should be updated. If the
   * `lastSyncedUpstreamValuesByRow` field is provided, then it should be the same
   * length as the number of rows in the `updatedCells`. Each element in the outside array of the
   * `lastSyncedUpstreamValuesByRow` will then be used to update that rows corresponding
   * `lastSyncedUpstreamValues` in the StructuredTable.
   *
   * If the `lastSyncedUpstreamValuesByRow` field is not provided, then no updates
   * will be made to the table's LastSyncedUpstreamValues.
   *
   * As part of the update, checks will be made to make sure that all of our
   * invariants on the table are kept.
   *
   * @param updatedCells - the updated table contents to be persisted
   * @param columnUpdates - any structural changes we should make to our columns, e.g. changing
   *                        the archived status or adding a new column
   * @param lastSyncedUpstreamValuesByRow - updates to the lastSyncedUpstreamValues for the rows
   * @param lastSyncedArchivePurposeByRow - updates to the lastSyncedArchivePurpose for the rows
   * @param isFinalizedByRow - updates to isFinalized for the rows
   * @param filters -
   * @param tableOp - the table edit operation representing the mutation that occurred.
   */
  updateHandler: (params: {
    updatedCells: Cells;
    updatedRowMetadatas?: Array<TRowMetadata>;
    numApiRowsToAdd?: number;
    columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
    lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>;
    lastSyncedArchivePurposeByRow?: Array<string | null | undefined>;
    isFinalizedByRow?: Array<boolean>;
    filters?: Array<StatusFilter>;
    tableOp?: TableOpDiff;
  }) => void;

  /**
   * The performTableOpOnTable callback can be used to perform certain kinds of complex edits to the table,
   * such as adding or removing columns. When performing these kinds of edits, we want to re-use
   * our library of "table ops". performTableOpOnTable is how we can access table ops from an action function.
   */
  performTableOpOnTable: ({op, options}: {op: TableOpFunction; options?: TableOpOptions}) => void;

  note?: Note | null;

  outsideNotebookContext?: OutsideNotebookContext | null;

  /** Additional info relating to where and how the action was invoked. */
  actionFunctionContext: ActionFunctionContext;

  isFilterStale: boolean;
}

/**
 * Parameters passed to action functions that operate at the level of the entire table, e.g. a toolbar button.
 */
export interface TableActionFunctionParameters<
  TTableMetadata,
  TRowMetadata,
> extends BaseActionFunctionParameters<TTableMetadata, TRowMetadata> {
  /**
   * The current cell selection, or null if no cells are selected.
   */
  multiSelectionBox: MultiSelectionBox | null;
  isCollapsed?: boolean;
}

/**
 * Parameters passed to action functions that operate at the level of an axis (row or column).
 *
 * Tech debt: The multiSelectionBox is not threaded through to context menu action functions. The
 * selectionRange provides equivalent information.
 */
export interface AxisContextMenuActionFunctionParameters<
  TTableMetadata,
  TRowMetadata,
> extends BaseActionFunctionParameters<TTableMetadata, TRowMetadata> {
  selectionRange: SelectionRange | null;
}

/**
 * @deprecated Use TableActionFunctionParameters, AxisContextMenuActionFunctionParameters, or
 * BaseActionFunctionParameters.
 */
export type ActionFunctionParameters<TTableMetadata, TRowMetadata> = TableActionFunctionParameters<
  TTableMetadata,
  TRowMetadata
>;

/**
 * An ActionFunction provides methods for rendering controls in the header of the StructuredTable.
 * ActionFunctions allow for extension of the StructuredTableView without having to build new React
 * components.  These can do things like call back end functions to register new entities, download a CSV
 * representation of the table or print off barcode labels for the items contained in the table.
 *
 * TECH NOTE: While ActionFunction methods look suspiciously like components (they take a params object and
 * return JSX), they are specifically *not* components:
 *  - They are not composable
 *  - They are not meant to be rendered by any component other than the StructuredTableView
 *  - Or to put it another way, they are not reusable other than specifically as an action function provided
 *     to a structured table
 */
export type ActionFunction<TTableMetadata, TRowMetadata> =
  | ToolbarButtonActionFunction<TTableMetadata, TRowMetadata>
  | OverflowMenuActionFunction<TTableMetadata, TRowMetadata>
  | RowContextMenuActionFunction<TTableMetadata, TRowMetadata>;

/**
 * An ActionFunction whose button is in the toolbar.
 */
export interface ToolbarButtonActionFunction<TTableMetadata, TRowMetadata> {
  renderToolbarButton: (
    params: TableActionFunctionParameters<TTableMetadata, TRowMetadata>
  ) => JSX.Element | null;
}

/**
 * An ActionFunction whose button is in the overflow menu.
 */
export interface OverflowMenuActionFunction<TTableMetadata, TRowMetadata> {
  getOverflowMenuItem: (
    params: TableActionFunctionParameters<TTableMetadata, TRowMetadata>
  ) => OverflowMenuOption | null;
}

/**
 * An ActionFunction that can be invoked from a row header cell's context menu.
 */
export interface RowContextMenuActionFunction<TTableMetadata, TRowMetadata> {
  getRowAxisContextMenuItem: (
    params: AxisContextMenuActionFunctionParameters<TTableMetadata, TRowMetadata>
  ) => StructuredTableContextMenuItem | null;
}

export interface StructuredTableContextMenuItem {
  name: string;
  onSelect: () => void;
}

/**
 * Several methods of adding filled rows to a table are available. These will be rendered as a dropdown
 * menu behind a [+] button.
 */
export const enum StructuredTableAddFromSourceType {
  ADD_FROM_TABLE,
  ADD_FROM_WORKLIST,
  ADD_FROM_SPREADSHEET,
  ADD_FROM_BARCODE_SCANNER,
  ADD_FROM_BOX_OR_PLATE,
  ADD_FROM_PARAMETER_LIST,
  ADD_FROM_MATERIAL_LIST,
  ADD_USING_AI,
}

// This object helps create the dropdown menu. It's used internally by StructuredTableView, and should
// not be needed elsewhere.
//
// Note that the name can be overridden using the property in StructuredTableAddOperationOptions
export const StructuredTableAddMenuItems: {
  [type in StructuredTableAddFromSourceType]: {
    name: string;
    icon: BenchlingIconName;
    dataTestAttrs: DataTestAttrs;
  };
} = {
  [StructuredTableAddFromSourceType.ADD_FROM_TABLE]: {
    name: 'Add from structured table',
    icon: 'table',
    dataTestAttrs: {component: 'StructuredTable', element: 'addFromStructuredTableMenuItem'},
  },
  [StructuredTableAddFromSourceType.ADD_FROM_WORKLIST]: {
    name: 'Add from worklist',
    icon: ModelTypeConstants.WORKLIST,
    dataTestAttrs: {component: 'StructuredTable', element: 'addFromWorklistMenuItem'},
  },
  [StructuredTableAddFromSourceType.ADD_FROM_SPREADSHEET]: {
    name: 'Add from spreadsheet',
    icon: 'upload',
    dataTestAttrs: {component: 'StructuredTable', element: 'addFromSpreadsheettMenuItem'},
  },
  [StructuredTableAddFromSourceType.ADD_FROM_BARCODE_SCANNER]: {
    name: 'Scan barcodes',
    icon: 'barcode',
    dataTestAttrs: {component: 'StructuredTable', element: 'scanBarcodeMenuItem'},
  },
  [StructuredTableAddFromSourceType.ADD_FROM_BOX_OR_PLATE]: {
    name: 'Add from a box or plate',
    icon: ModelTypeConstants.BOX,
    dataTestAttrs: {component: 'StructuredTable', element: 'addFromBoxOrPlateMenuItem'},
  },
  [StructuredTableAddFromSourceType.ADD_FROM_PARAMETER_LIST]: {
    name: 'Add parameters',
    icon: 'parameters',
    dataTestAttrs: {component: 'StructuredTable', element: 'addFromParameterListMenuItem'},
  },
  [StructuredTableAddFromSourceType.ADD_FROM_MATERIAL_LIST]: {
    name: 'Add materials',
    icon: 'material-input',
    dataTestAttrs: {component: 'StructuredTable', element: 'addFromParameterListMenuItem'},
  },
  [StructuredTableAddFromSourceType.ADD_USING_AI]: {
    name: 'Data Entry Agent',
    icon: 'magic-wand',
    dataTestAttrs: {component: 'StructuredTable', element: 'addUsingAI'},
  },
};

export interface StructuredTableAddOperationContexts {
  procedureStepConfigEditorContext?: ProcedureStepConfigEditorContextValue;
}

// The requirements of each "add from source" menu. What info does it need to do its work?
export interface StructuredTableAddOperationParameters<TTableMetadata, TRowMetadata> {
  // The note the table belongs to, or null if the table does not belong to a note.
  note: Note | null;
  // The source table being added to
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  // The samples to be added need to be converted to Cells first
  addSamples: (
    samplesByColumn: Array<{samples: Array<Cell>; columnIndex: number}>,
    options?: AddToTableOptions
  ) => void;
  // Some add operations (like add from spreadsheet, or adding rows with delegated columns)
  // need to add a full row at a time, along with other table data
  addFullRows: (
    rows: Array<Row>,
    rowMetadatas?: Array<TRowMetadata>,
    delegateColumns?: Array<StructuredTableColumn>,
    structuredTableUpdates?: Partial<
      Pick<StructuredTable<TTableMetadata, TRowMetadata>, 'metadata' | 'schemas'>
    >
  ) => void;
  options?: StructuredTableAddOperationOptions;
  contexts: StructuredTableAddOperationContexts;
  insertAfterTable: (noteParts: Array<NotePart>, cIdIdMap?: CidIdMap) => void;
}

export interface StructuredTableAddOperationOptions {
  /**
   * In some special cases, we only want to support UAS to the extent of populating existing rows and not adding
   * additional ones, e.g. in the Workflows Task table in notebook entries. This interface allows the caller to
   * specify whether theirs is such a special case, and if so, how to behave.
   */
  preventTableExpansion?: boolean;
  preventTableExpansionMessage?: string;
  /**
   * The static names in StructuredTableAddMenuItems (above) may not suffice for all cases.
   */
  menuItemNameOverride?: string;
}

export interface StructuredTableAddOperation<TTableMetadata, TRowMetadata> {
  type: StructuredTableAddFromSourceType;
  onSelect: (
    params: StructuredTableAddOperationParameters<TTableMetadata, TRowMetadata>
  ) => void | Promise<void>;
  options?: StructuredTableAddOperationOptions;
  dataTestAttrs?: DataTestAttrs;
}

export interface StructuredTableColumnSummaryRequirement {
  columnType: TableColumnType;
  schemaFieldType: FieldType | null;
  folderItemType: string | null;
}

// TODO(notebook, processes): re-evaluate whether this interface should be part of core notebook code.
export interface StructuredTableColumnSummary {
  /**
   * The index of the column being summarized
   * Optional: An index will be present if this object corresponds to a column in a table. However this can
   * also be used to describe the expected shape of a table column based on an item (eg entity, storable) in
   * which case this property will be undefined.
   */
  columnIndex?: number;

  /**
   * validDestinationColumnRequirements describe what properties a valid destination column
   * must satisfy. Given that different tables describe columns in different ways,
   * validDestinationColumnRequirements is intended to provide flexibility in that if a destination
   * column satisfies at least one column requirement, it is a match to the summarized column.
   * columnType is a TableColumnType and must always be present in a column requirement.
   * schemaFieldType and folderItemType can be used to further specify what requirements a column
   * must have. If they are null, then the are intentionally unspecified and can match any values.
   */
  validDestinationColumnRequirements: Array<StructuredTableColumnSummaryRequirement>;

  /**
   * The schema ID of the column being summarized, used to check against destination columns with a
   * schema ID constraint
   */
  schemaIdConstraint: string | null;

  /**
   * When shouldFetchPlateInfo is true, the column is summarizing a plate and well, which means
   * we must query for more specific information (e.g. container ID).
   */
  shouldFetchPlateInfo?: boolean;
}

export type BaseStringStructuredTableColumn<TIds> = {
  columnType: TableColumnType.STRING;
  tagSchemaFieldId?: null;
} & BaseStructuredTableColumn<TIds>;

export interface ExistingBioentityStructuredTableColumnFrontEndMetadata {
  // Useful if bioentities in this column should be of a different schema than that of the table (e.g. in a
  // results table).
  validSchemas?: Array<TagSchemaSummary> | 'ALL';
  // Used for validation when bioentity is looked up as contents of container/plate well
  storableSchemaId?: string | null;
}

export type BaseExistingBioentityStructuredTableColumn<TIds> = {
  columnType: TableColumnType.EXISTING_BIOENTITY;
  bioentityTypeFilters?: Array<BioentityType>;
} & BaseStructuredTableColumn<TIds, ExistingBioentityStructuredTableColumnFrontEndMetadata>;

export type ExistingBioentityStructuredTableColumn = BaseExistingBioentityStructuredTableColumn<ClientId>;

export type BaseBioentitySelectorStructuredTableColumn<TIds> = {
  columnType: TableColumnType.BIOENTITY_SELECTOR;
  schemaFieldForFiltering: SchemaFieldDataType | null;
} & BaseStructuredTableColumn<TIds>;

export type BioentitySelectorStructuredTableColumn = BaseBioentitySelectorStructuredTableColumn<ClientId>;

interface TagValueStructuredTableColumnFrontEndMetadata {
  // Used for validation when tag is looked up from potentially invalid plate well
  storableSchemaId?: string | null;
}

export type BaseTagValueStructuredTableColumn<TIds> = {
  columnType: TableColumnType.TAG_VALUE;

  schemaFieldType: FieldType;
  schemaFieldDisplayName: string;
  schemaFieldDescription?: string | null;
  schemaFieldDecimalPrecision?: number | null;
  schemaFieldIsComputed: boolean;
  schemaFieldIsConvertedFromLink: boolean | null; // null for pre-existing, non-converted-from-link fields
  schemaFieldIsSnapshot: boolean;
  snapshotFieldInputSchemaFieldApiId: string | null;
  snapshotFieldGridPositionInputSchemaFieldApiId: string | null;
  snapshotFieldInputIsProcessTask: boolean;
  schemaFieldApiId: string;
  // TODO(ala): tagSchemaFieldId should be deprecated and we should be using schemaFieldApiId instead
  tagSchemaFieldId: number;
  unitApiIdentifier?: string | null;

  selector: SchemaFieldSelector | null;
} & BaseStructuredTableColumn<TIds, TagValueStructuredTableColumnFrontEndMetadata>;

/**
 * Structured table column representing a TagValue for which the schema field has been deleted. This should be
 * only possible for Data Table Config columns, but we don't have a DB constraint to enforce it
 */
export type BaseTagValueStructuredTableColumnWithMissingField<TIds> = {
  columnType: TableColumnType.TAG_VALUE;

  // The snapshotted values would be null if the table has been refreshed after the schema field was deleted
  schemaFieldType: FieldType | null;
  schemaFieldDisplayName: string | null;
  schemaFieldDescription: string | undefined | null;
  schemaFieldDecimalPrecision?: number | null;
  schemaFieldIsComputed: boolean | null;
  schemaFieldIsConvertedFromLink: boolean | null; // null for pre-existing, non-converted-from-link fields
  schemaFieldIsSnapshot: boolean | null;
  snapshotFieldInputSchemaFieldApiId: string | null;
  snapshotFieldGridPositionInputSchemaFieldApiId: string | null;
  snapshotFieldInputIsProcessTask: boolean;
  schemaFieldApiId: string | null;

  // Props corresponding to the live value of tag_schema_field, which is null in this case
  tagSchemaFieldId: null;
  selector: null;
} & BaseStructuredTableColumn<TIds, TagValueStructuredTableColumnFrontEndMetadata>;

export type TagValueStructuredTableColumn =
  | BaseTagValueStructuredTableColumn<ClientId>
  | BaseTagValueStructuredTableColumnWithMissingField<ClientId>;

export interface ComplexPolymerComponentFrontEndMetadata {
  allowsMultipleComponents: boolean;
}

export type BaseComplexPolymerComponentStructuredTableColumn<TIds> = {
  columnType: TableColumnType.COMPLEX_POLYMER_COMPONENT;

  schemaFieldApiId: string;
} & BaseStructuredTableColumn<TIds, ComplexPolymerComponentFrontEndMetadata>;

export type ComplexPolymerComponentStructuredTableColumn =
  BaseComplexPolymerComponentStructuredTableColumn<ClientId>;

export type BaseQuantityStructuredTableColumn<TIds> = {
  columnType: TableColumnType.QUANTITY;
  units: string;
  unitApiIdentifier?: string;
} & BaseStructuredTableColumn<TIds>;

export type QuantityStructuredTableColumn = BaseQuantityStructuredTableColumn<ClientId>;

export type BaseConcentrationStructuredTableColumn<TIds> = {
  columnType: TableColumnType.CONCENTRATION;
  units: string;
  unitApiIdentifier?: string;
} & BaseStructuredTableColumn<TIds>;

export type ConcentrationStructuredTableColumn = BaseConcentrationStructuredTableColumn<ClientId>;

interface ContainersColumnFrontEndMetadata {
  containerSchemaId: string;
  containerSchemaName: string;
}

export type BaseContainersStructuredTableColumn<TIds> = {
  columnType: TableColumnType.CONTAINERS;
} & BaseStructuredTableColumn<TIds, ContainersColumnFrontEndMetadata>;

export type ContainersStructuredTableColumn = BaseContainersStructuredTableColumn<ClientId>;

interface UnitSelectorColumnFrontEndMetadata {
  acceptedUnits: Array<string>;
  storableSchemaId?: string | null;
}

export type BaseUnitSelectorStructuredTableColumn<TIds> = {
  columnType: TableColumnType.UNIT_SELECTOR;
} & BaseStructuredTableColumn<TIds, UnitSelectorColumnFrontEndMetadata>;

export type UnitSelectorStructuredTableColumn = BaseUnitSelectorStructuredTableColumn<ClientId>;

export enum UnitReferenceColumnDisplayMode {
  /** Displays the unit as a chip with the full unit name and symbol */
  CHIP = 'CHIP',
  /** Shows only the symbol as text with a tooltip showing the full unit name */
  SYMBOL = 'SYMBOL',
}
export interface UnitReferenceColumnFrontEndMetadata {
  storableSchemaId?: string | null;
  unitTypesFilterPredicate?: (unitType: object) => boolean;
  ignoreErrorWhenTypedValue?: TypedValue;
  displayMode?: UnitReferenceColumnDisplayMode;
}

export type BaseUnitReferenceStructuredTableColumn<TIds> = {
  columnType: TableColumnType.UNIT_REFERENCE;
} & BaseStructuredTableColumn<TIds, UnitReferenceColumnFrontEndMetadata>;

export type UnitReferenceStructuredTableColumn = BaseUnitReferenceStructuredTableColumn<ClientId>;
interface MeasurementAmountColumnFrontEndMetadata {
  isComputed?: boolean;
  allowQS: boolean;
  allowZero?: boolean;
  storableSchemaId?: string | null;
}

export type BaseMeasurementAmountStructuredTableColumn<TIds> = {
  columnType: TableColumnType.MEASUREMENT_AMOUNT;
} & BaseStructuredTableColumn<TIds, MeasurementAmountColumnFrontEndMetadata>;

export type MeasurementAmountStructuredTableColumn = BaseMeasurementAmountStructuredTableColumn<ClientId>;

export type BaseOptionSelectorStructuredTableColumn<TIds> = {
  columnType: TableColumnType.OPTION_SELECTOR;
  options: Array<string | OptionCellDropdownOption>;
} & BaseStructuredTableColumn<TIds>;

export type OptionSelectorStructuredTableColumn = BaseOptionSelectorStructuredTableColumn<ClientId>;

export type BaseOptionSearchSelectorStructuredTableColumn<TIds> = {
  columnType: TableColumnType.OPTION_SEARCH_SELECTOR;
  options: Array<string | OptionData>;
  isMulti?: boolean;
} & BaseStructuredTableColumn<TIds>;

export type OptionSearchSelectorStructuredTableColumn =
  BaseOptionSearchSelectorStructuredTableColumn<ClientId>;

interface ParentStorableStructuredTableColumnFrontEndMetadata {
  // Used for validation when parent storable is looked up from potentially invalid container/plate well
  storableSchemaId?: string | null;
}

export type BaseParentStorableStructuredTableColumn<TIds> = {
  columnType: TableColumnType.PARENT_STORABLE;
} & BaseStructuredTableColumn<TIds, ParentStorableStructuredTableColumnFrontEndMetadata>;

export type ParentStorableStructuredTableColumn = BaseParentStorableStructuredTableColumn<ClientId>;

interface PositionStructuredTableColumnFrontEndMetadata {
  // Used for validation when position in parent storable is looked up from potentially invalid container/plate well
  storableSchemaId?: string | null;
}

export type BasePositionStructuredTableColumn<TIds> = {
  columnType: TableColumnType.POSITION;
} & BaseStructuredTableColumn<TIds, PositionStructuredTableColumnFrontEndMetadata>;

export type PositionStructuredTableColumn = BasePositionStructuredTableColumn<ClientId>;

export type BoxStructuredTableColumn = {
  columnType: TableColumnType.BOX;
} & BaseStructuredTableColumn<ClientId>;

export type PlateStructuredTableColumn = {
  columnType: TableColumnType.PLATE;
} & BaseStructuredTableColumn<ClientId>;

interface PartiesStructuredTableColumnFrontEndMetadata {
  // Used for validation when parties (currently sample owners / authorized users) are looked up from
  // potentially invalid container / plate well
  storableSchemaId?: string | null;
}

export type BasePartiesStructuredTableColumn<TIds> = {
  columnType: TableColumnType.PARTIES;
} & BaseStructuredTableColumn<TIds, PartiesStructuredTableColumnFrontEndMetadata>;

export type PartiesStructuredTableColumn = BasePartiesStructuredTableColumn<ClientId>;

interface SampleRestrictionStatusStructuredTableColumnFrontEndMetadata {
  // Used for validation when sample restriction status is looked up from
  // potentially invalid container / plate well
  storableSchemaId?: string | null;
}

export type BaseSampleRestrictionStatusStructuredTableColumn<TIds> = {
  columnType: TableColumnType.SAMPLE_RESTRICTION_STATUS;
} & BaseStructuredTableColumn<TIds, SampleRestrictionStatusStructuredTableColumnFrontEndMetadata>;

export type SampleRestrictionStatusStructuredTableColumn =
  BaseSampleRestrictionStatusStructuredTableColumn<ClientId>;

export type BaseCustomStructuredTableColumn<TIds> = {
  columnType: TableColumnType.CUSTOM;
  name: string;
  customColumnConfig: CustomColumnConfig;
} & BaseStructuredTableColumn<TIds>;

export type CustomStructuredTableColumn = BaseCustomStructuredTableColumn<ClientId>;

export type BaseBasesStructuredTableColumn<TIds> = {
  columnType: TableColumnType.BASES;
  basesColumnConfig: BasesColumnConfig;
} & BaseStructuredTableColumn<TIds>;

export type BasesStructuredTableColumn = BaseBasesStructuredTableColumn<ClientId>;

export type BaseBulkAssemblyBinSelectorStructuredTableColumn<TIds> = {
  columnType: TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR;
  bins: Array<BulkAssemblyFragmentBin>;
} & BaseStructuredTableColumn<TIds>;

export type BulkAssemblyBinSelectorStructuredTableColumn =
  BaseBulkAssemblyBinSelectorStructuredTableColumn<ClientId>;

export type BaseBulkAssemblyFragmentEndpointSelectorStructuredTableColumn<TIds> = {
  columnType: TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT;
  side: Side;
} & BaseStructuredTableColumn<TIds>;

export type BulkAssemblyFragmentEndpointSelectorStructuredTableColumn =
  BaseBulkAssemblyFragmentEndpointSelectorStructuredTableColumn<ClientId>;

export type BaseBulkAssemblyFragmentSequenceStructuredTableColumn<TIds> = {
  columnType: TableColumnType.BULK_ASSEMBLY_FRAGMENT_POLYMER;
  bioentityTypeFilters: Array<BioentityType>;
} & BaseStructuredTableColumn<TIds>;

export type BulkAssemblyFragmentSequenceStructuredTableColumn =
  BaseBulkAssemblyFragmentSequenceStructuredTableColumn<ClientId>;

export type BaseBulkAssemblyPreferredPrimerStructuredTableColumn<TIds> = {
  columnType: TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER;
} & BaseStructuredTableColumn<TIds>;

export type BulkAssemblyPreferredPrimerStructuredTableColumn =
  BaseBulkAssemblyPreferredPrimerStructuredTableColumn<ClientId>;

export type BaseBulkAssemblyRestrictionEnzymeStructuredTableColumn<TIds> = {
  columnType: TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME;
} & BaseStructuredTableColumn<TIds>;

export type BulkAssemblyRestrictionEnzymeStructuredTableColumn =
  BaseBulkAssemblyRestrictionEnzymeStructuredTableColumn<ClientId>;

export type BaseBulkAssemblyFragmentSelectorStructuredTableColumn<TIds> = {
  columnType: TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR;
  fragmentDatas: Array<SpecifiedFragmentInfo>;
  binColor: string;
  method: BulkAssemblyMethod;
  assemblyId: string;
} & BaseStructuredTableColumn<TIds>;

export type BulkAssemblyFragmentSelectorStructuredTableColumn =
  BaseBulkAssemblyFragmentSelectorStructuredTableColumn<ClientId>;

export type BaseFillPlateSourceContainerChipStructuredTableColumn<TIds> = {
  columnType: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP;
  frozenEntityChipData: {[id: string]: FrozenEntityChipData};
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  testOrderInfoById: TestOrderInfoById;
} & BaseStructuredTableColumn<TIds>;

export type FillPlateSourceContainerChipStructuredTableColumn =
  BaseFillPlateSourceContainerChipStructuredTableColumn<ClientId>;

export type BaseFillPlateSourceChipStructuredTableColumn<TIds> = {
  columnType: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP;
  frozenEntityChipData?: {[id: string]: FrozenEntityChipData};
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  testOrderInfoById: TestOrderInfoById;
} & BaseStructuredTableColumn<TIds>;

export type FillPlateSourceChipStructuredTableColumn = BaseFillPlateSourceChipStructuredTableColumn<ClientId>;

export type BasePlateMapMaterialInputStructuredTableColumn<TIds> = {
  columnType: TableColumnType.PLATE_MAP_MATERIAL_INPUT;
  plateMaterialInputs: Array<PlateMaterialInput>;
} & BaseStructuredTableColumn<TIds>;

export type PlateMapMaterialInputStructuredTableColumn =
  BasePlateMapMaterialInputStructuredTableColumn<ClientId>;

export type BaseProcedureParameterConfirmedInStructuredTableColumn<TIds> = {
  columnType: TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN;
  navigateToStep: (stepCid: string) => void;
} & BaseStructuredTableColumn<TIds>;

export type ProcedureParameterConfirmedInStructuredTableColumn =
  BaseProcedureParameterConfirmedInStructuredTableColumn<ClientId>;

export type BaseTestDefinitionStructuredTableColumn<TIds> = {
  columnType: TableColumnType.TEST_DEFINITION;
  targetOrganization?: {id: string; name: string};
} & BaseStructuredTableColumn<TIds>;

export type TestDefinitionStructuredTableColumn = BaseTestDefinitionStructuredTableColumn<ClientId>;

export type LookupConfig = LookupConfigSpec & {
  id: string;
};

export type BaseDedicatedLookupStructuredTableColumn<TIds> = {
  columnType: TableColumnType.DEDICATED_LOOKUP;
  inputColumnProvenanceUuid: string;
  lookupConfig: LookupConfig;
  unitApiIdentifier?: string | null;
  lookupSortType?: LookupSortType;
} & BaseStructuredTableColumn<TIds>;

export type DedicatedLookupStructuredTableColumn = BaseDedicatedLookupStructuredTableColumn<ClientId>;

interface ProcessTaskStatusColumnFrontEndMetadata {
  statusLifecycle?: ProcessTaskStatusLifecycleGraphQL;
}

export type BaseProcessTaskStatusStructuredTableColumn<TIds> = {
  columnType: TableColumnType.PROCESS_TASK_STATUS;
} & BaseStructuredTableColumn<TIds, ProcessTaskStatusColumnFrontEndMetadata>;

export type ProcessTaskStatusStructuredTableColumn = BaseProcessTaskStatusStructuredTableColumn<ClientId>;

interface ProcessTaskColumnFrontEndMetadata {
  schemaId: string | null;
}

type BaseProcessTaskStructuredTableColumn<TIds> = {
  columnType: TableColumnType.PROCESS_TASK;
} & BaseStructuredTableColumn<TIds, ProcessTaskColumnFrontEndMetadata>;

export type ProcessTaskStructuredTableColumn = BaseProcessTaskStructuredTableColumn<ClientId>;

type BaseProcessOutputStructuredTableColumn<TIds> = {
  columnType: TableColumnType.PROCESS_OUTPUT;
} & BaseStructuredTableColumn<TIds>;

export type ProcessOutputStructuredTableColumn = BaseProcessOutputStructuredTableColumn<ClientId>;

type BaseRowDelegateStructuredTableColumn<TIds> = {
  columnType: TableColumnType.ROW_DELEGATE;
} & BaseStructuredTableColumn<TIds>;

type BaseProcedureStructuredTableColumn<TIds> = {
  columnType: TableColumnType.PROCEDURE;
  procedureTypes?: Array<ProcedureType>;
  placeholder?: string;
  shouldAllowMultiple?: boolean;
} & BaseStructuredTableColumn<TIds>;

export type ProcedureStructuredTableColumn = BaseProcedureStructuredTableColumn<ClientId>;

type BaseProcedureConditionReplicatesStructuredTableColumn<TIds> = {
  columnType: TableColumnType.PROCEDURE_CONDITION_REPLICATES;
} & BaseStructuredTableColumn<TIds>;

export type ProcedureConditionReplicatesStructuredTableColumn =
  BaseProcedureConditionReplicatesStructuredTableColumn<ClientId>;

type BaseProcedureSchemaFieldTypeStructuredTableColumn<TIds> = {
  columnType: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE;
  context: ProcedureSchemaFieldTypeContext;
} & BaseStructuredTableColumn<TIds>;

export type ProcedureSchemaFieldTypeStructuredTableColumn =
  BaseProcedureSchemaFieldTypeStructuredTableColumn<ClientId>;

export type RowDelegateStructuredTableColumn = BaseRowDelegateStructuredTableColumn<ClientId>;

interface AntibodyDomainColumnMetadata {
  domainType: AntibodyDomainType;
  specificity: string | null;
}

export type BaseAntibodyDomainStructuredTableColumn<TIds> = {
  columnType: TableColumnType.ANTIBODY_DOMAIN;
  columnMetadata: AntibodyDomainColumnMetadata;
} & BaseStructuredTableColumn<TIds>;

export type AntibodyDomainStructuredTableColumn = BaseAntibodyDomainStructuredTableColumn<ClientId>;

export type GenericStructuredTableColumn<TIds> =
  | BaseStructuredTableColumn<TIds>
  | BaseBasesStructuredTableColumn<TIds>
  | BaseBulkAssemblyBinSelectorStructuredTableColumn<TIds>
  | BaseBulkAssemblyFragmentEndpointSelectorStructuredTableColumn<TIds>
  | BaseBulkAssemblyFragmentSequenceStructuredTableColumn<TIds>
  | BaseBulkAssemblyRestrictionEnzymeStructuredTableColumn<TIds>
  | BaseConcentrationStructuredTableColumn<TIds>
  | BaseContainersStructuredTableColumn<TIds>
  | BaseCustomStructuredTableColumn<TIds>
  | BaseDedicatedLookupStructuredTableColumn<TIds>
  | BaseExistingBioentityStructuredTableColumn<TIds>
  | BaseBioentitySelectorStructuredTableColumn<TIds>
  | BaseFillPlateSourceContainerChipStructuredTableColumn<TIds>
  | BaseMeasurementAmountStructuredTableColumn<TIds>
  | BaseOptionSelectorStructuredTableColumn<TIds>
  | BaseParentStorableStructuredTableColumn<TIds>
  | BasePartiesStructuredTableColumn<TIds>
  | BasePositionStructuredTableColumn<TIds>
  | BaseProcessTaskStatusStructuredTableColumn<TIds>
  | BaseProcessTaskStructuredTableColumn<TIds>
  | BaseQuantityStructuredTableColumn<TIds>
  | BaseSampleRestrictionStatusStructuredTableColumn<TIds>
  | BaseStringStructuredTableColumn<TIds>
  | BaseTagValueStructuredTableColumn<TIds>
  | BaseTagValueStructuredTableColumnWithMissingField<TIds>
  | BaseUnitSelectorStructuredTableColumn<TIds>
  | BaseRowDelegateStructuredTableColumn<TIds>
  | BaseProcedureStructuredTableColumn<TIds>
  | BaseProcedureConditionReplicatesStructuredTableColumn<TIds>
  | BaseProcedureSchemaFieldTypeStructuredTableColumn<TIds>
  | BaseComplexPolymerComponentStructuredTableColumn<TIds>
  | BaseAntibodyDomainStructuredTableColumn<TIds>;

export type StructuredTableColumn = GenericStructuredTableColumn<ClientId>;
export type ToServerStructuredTableColumn = GenericStructuredTableColumn<ToServerId>;
export type FromServerStructuredTableColumn = GenericStructuredTableColumn<FromServerId>;

export const DATA_TABLE_COLUMN_SHAPE = PropTypes.shape({
  cid: PropTypes.string,
  columnType: PropTypes.oneOf(Object.values(TableColumnType)).isRequired,
  defaultValue: cell.isRequired,
  name: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  tagSchemaFieldId: PropTypes.number,
  isReported: PropTypes.bool.isRequired,
});

/**
 * The LastSyncedUpstreamValue gives us a "base" value for a particular cell in a row.  We can
 * identify which cell by matching the `columnProvenanceUuid` and `fieldId`. The
 * TypedValue in the `value` property was the value of the cell at the time the row was synced.
 * `columnType`, `fieldId` and `columnGroup` were previously used to locate the cell before
 * `columnProvenanceUuid` was added
 */
export interface LastSyncedUpstreamValue {
  // The column group for the column which contains this value
  columnGroup: ColumnGroup | null;

  // The TableColumnType for the column where this value is contained
  columnType: TableColumnType;

  // This should be the tagSchemaField API identifier for the value
  fieldId: string | null;

  // This is the UUID of the column that contains the value
  columnProvenanceUuid: string | null;

  // The actual contents of the cell when the row was synced.
  value: TypedValue | null;

  // The time at which the value was captured
  submittedAt: string;
}

/**
 * An extensible row interface for StructuredTable.  This allows us to
 * extend additional metadata beyond just isFinalized for specific
 * StructuredTable implementations.
 */
export interface StructuredTableRow<TRowMetadata = unknown> {
  // Has the row ever been acted upon? (e.g. registered in a registration table.) The structured table
  // implementation may lock cells in a finalized row to prevent editing, but is not required to.
  isFinalized: boolean;

  // The rowId should be a unique id within the table.  We use a UUID here to help ensure it's unique
  rowId: string;
  cells: Array<Cell>;
  metadata: TRowMetadata;

  /**
   * The archive purpose of the row, if archived, otherwise null.
   * If undefined, the archived state is unknown.
   *
   * Note: This property represents the state at the time of this table's respective entry version. This may
   * be out of sync from the upstream state of the row's archive purpose, which is what should be used for
   * rendering.
   */
  lastSyncedArchivePurpose?: string | null;

  /**
   * The lastSyncedUpstreamValues contains the "base" values for the row contained in the row.  This
   * value is updated when a row is submitted or when it's refreshed.  The intention of this is to
   * allow us to know when the row's cells have been updated locally or when the values are "stale"
   * as the schema taggable or some other entity has been updated outside of the table since the
   * value was synced with the table.
   *
   * If this value is undefined, then the row has not yet been backfilled to contain the appropriate
   * lastSyncedUpstreamValues.
   *
   * If the value is null, then the row has never been synced.
   */
  lastSyncedUpstreamValues?: Array<LastSyncedUpstreamValue> | null;
}

export type UnknownStructuredTableRow = StructuredTableRow<unknown>;

/**
 * A collection of row metadata that is fetched "live" (as opposed to being versioned with the entry).
 */
export interface LiveRowMetadata {
  /**
   * The archive purpose returned by the RowUpdater, if any. May be undefined while the fetch is in progress.
   */
  archivePurpose?: string | null;
}

/**
 * This metadata is used to match schemas to their column group and is
 * used for all StructuredTables.
 */
export interface StructuredTableSchemaMetadata {
  schemaId: string;
  labelingStrategy?: string | null;
  defaultProjectId?: string | null;
  group: ColumnGroup;
  parentSchemaId?: string | null;
}

/**
 * Metadata which is needed for every StructuredTable.
 *
 * @property schemaMetadata
 * A mapping between schemaIds and their column groups, as well as
 * labeling strategy and defaultProjectId.
 * @property lockRowCount
 * When lockedRowCount is false or undefined, rows can be added freely
 * to the table. When it is true, the table will be locked at its current
 * length; UAS operations and editing are still allowed, but rows cannot be
 * deleted or inserted, and UAS operations fail if they try to add more rows.
 * @property tags
 * tag metadata signals to the consumer that the table conforms to some predefined structure, which
 * is currently not expressable with schema or some other ways.
 * e.g. "TEST_ORDER_TABLE" contains a specific set of columns and is used for test orders.
 */
/**
 * Base metadata for all structured tables.
 *
 * Keep in sync with benchling/notebook/structured_tables/constants/structured_tables.py
 */
export interface StructuredTableMetadata {
  schemaMetadata: Array<StructuredTableSchemaMetadata>;
  lockRowCount?: boolean;
  tags?: Array<string>;
  importSources?: Array<ImportSource>;
}

/**
 * Metadata to track where data in a table was added from.
 *
 * Keep in sync with benchling/notebook/structured_tables/constants/structured_tables.py
 */
export interface ImportSource {
  apiId: string;
}

export interface StructuredTableTabularData<TRowMetadata, TIds> {
  // The name of the table which is used in the header and in cross
  // table references
  name: string;

  // The columns which are currently present on the table
  columns: Array<GenericStructuredTableColumn<TIds>>;

  // Columns that are used for specific cells' behavior, but are not visibly present on the table
  delegatedColumns: Array<GenericStructuredTableColumn<TIds>>;

  // The actual table data
  rows: Array<StructuredTableRow<TRowMetadata>>;
}

export type StructuredTableSchema = Schema | BatchSchema;

export interface StructuredTableNonTabularData<TTableMetadata> {
  /**
   * The particular type of structured table, which is used to look up the StructuredTableAdapter to use
   */
  kind: StructuredTableKind;

  /**
   * A UUID that is shared across all versions of the table, both templates and their derivitives.
   */
  provenanceUuid: string;

  /**
   * An API ID that denotes the file that this table is associated with/lives inside.
   */
  fileId?: string;

  /**
   * The API ID of the structured table.
   */
  apiId?: string;

  /**
   * The number of pending new API added results.
   */
  numApiRowsToAdd?: number;

  /**
   * Whether this table belongs to a configuration document.
   */
  isConfig: boolean;

  /** The schemas associated with this table.
   * This affects the initial columns shown as well as what columns could be added.
   * This list only contains schemas that the current user has access to.
   */
  schemas: Array<StructuredTableSchema>;

  /**
   * Additional details particular to the kind that can be used for the StructuredTable
   * This is logged in the CREATED_STRUCTURED_TABLE analytics event, so it should
   * not have any sensitive information like names.
   */
  metadata: StructuredTableMetadata & TTableMetadata;

  filterInfo?: FilterInfo;
}

export interface FilterInfo {
  // rows that are filtered out and will not be shown
  filteredRowIds: Array<string>;
  // (OR filters): only rows that satisfy at least one filter will be shown.
  filters: Array<StatusFilter>;
}

export enum StatusFilter {
  HAS_ERROR = 'contains error',
  IS_UNSUBMITTED = 'unsubmitted',
  IS_OUT_OF_DATE = 'out of date',
}

/**
 * A base interface for tables in Benchling.  A StructuredTable is
 * some table whose contents are determined by a set of Schemas and
 * their fields in Benchling.  As this is an extensible interface, it
 * can take two type parameters:
 *
 * - TTableMetadata - what additional details do we need to store
 *                    about the table
 * - TRowMetadata - what additional details do we need to know about
 *                  each row in the table
 *
 * Each table has a set of schemas that are to be represented in the
 * table's columns.  These may not represent the actual columns which
 * are used as columns could be removed but they are used to determine
 * the initial columns.
 */
export type BaseStructuredTable<TTableMetadata, TRowMetadata, TIds> = {
  type: NoteLinkedObjectType.STRUCTURED_TABLE;
} & StructuredTableTabularData<TRowMetadata, TIds> &
  StructuredTableNonTabularData<TTableMetadata> &
  NLOId<TIds>;

export type StructuredTable<TTableMetadata, TRowMetadata> = BaseStructuredTable<
  TTableMetadata,
  TRowMetadata,
  ClientId
> &
  StructuredTableTabularData<TRowMetadata, ClientId> &
  StructuredTableNonTabularData<TTableMetadata>;

export type UnknownStructuredTable = StructuredTable<unknown, unknown>;

export type ToServerStructuredTable<TTableMetadata, TRowMetadata> = BaseStructuredTable<
  TTableMetadata,
  TRowMetadata,
  ToServerId
> &
  StructuredTableTabularData<TRowMetadata, ToServerId> &
  Omit<StructuredTableNonTabularData<TTableMetadata>, 'schemas'>;

export type FromServerStructuredTable<TTableMetadata, TRowMetadata> = BaseStructuredTable<
  TTableMetadata,
  TRowMetadata,
  FromServerId
> &
  StructuredTableTabularData<TRowMetadata, FromServerId>;

export type DefaultValuesByColumnType = Partial<Record<TableColumnType, Cell>>;

/**
 * These props are used to create StructuredTables by the
 * StructuredTableAdapter::createTable method.  They wrap around a
 * particular TTableMetadata which is an extension method for adding
 * additional data to StructuredTables.
 */
export interface CreateStructuredTableProps<TTableMetadata> {
  // The name which we will give to the StructuredTable created from
  // these props
  name: string;

  // The schemas from which we will create columns
  schemas: Array<SchemaAndColumnGroup>;

  // The entryId of the containing entry or stage entry. If null, the table isn't in an entry.
  entryId: string | null;

  // The metadata which we need for the StructuredTable.  This could
  // be used to determine which columns to use initially for the
  // table.
  metadata: TTableMetadata;

  // The selectors for the various schema fields contained in the
  // StructuredTable
  selectorsById: {[selectorId: string]: SchemaFieldSelector};

  // StructuredTableColumn['defaultValue'] values to set on
  // columns by TableColumnType on table creation.
  defaultValuesByColumnType?: DefaultValuesByColumnType;

  // Should this StructuredTable be created in a configuration mode or an execution mode?  Configuration mode
  // is generally within a Template where the behavior of the table is different, i.e. it cannot be submitted and we
  // set the default values of the column if there is just a single row.
  isConfig?: boolean;

  /**
   * Additional context about where the table is being created. This can be used to toggle specific
   * functionality, for example, if the table is embedded in a procedure step.
   *
   * This context is currently only passed by a consumer in specific cases where table functionality depends on it.
   * In all other cases, an empty default is provided. If you need to depend on this context, please verify
   * the DOM tree containing the table has a StructuredTableInsertionContext consumer.
   * See {@link StructuredTableInsertionContext} for more details.
   */
  structuredTableInsertionContext: StructuredTableInsertionContextValue;
}

/**
 * A PrimarySample is a name for some bioentity, storable or other item whose fields or attributes
 * are represented in a row of a StructuredTable.  A StructuredTableRow may contain many
 * PrimarySamples.
 *
 * In general, the columns that are part of a StructuredTable will be related to the PrimarySample.
 * For instance, the TAG_VALUE columns of a StructuredTable will all relate to the fields of the
 * PrimarySample of that row in a RegistrationTable or PlateCreationTable.  Or we may have some
 * attributes that are not TAG_VALUE columns but are derived from the row metadata, like the catalog
 * number for an ingredient in the Mixture Ingredients table.
 */
export interface PrimarySample {
  schemaId: string | null;
  columnGroup: ColumnGroup | null;
  sampleLookupInfo: SampleLookupInfo | null;
}

export interface DirectLookupInfo {
  idForLookup: string;
  sampleType: PrimarySampleLookupType.DIRECT;
  customSyntaxName?: string | null;
  isUnfinalizedParentStorable?: boolean;
}
export interface PlatePositionLookupInfo {
  idForLookup: string;
  // The string type should be used when we don't have enough info about the plate to
  // parse a plate well position string (e.g. 'A1' or '17')
  plateWellPosition: Position | string;
  sampleType: PrimarySampleLookupType.PLATE_POSITION_DUPLE;
}
export type SampleLookupInfo = DirectLookupInfo | PlatePositionLookupInfo;

export enum PrimarySampleLookupType {
  // Should be used when idForLookup would be that of the primarySample
  DIRECT = 'DIRECT',
  // Should be used when idForLookup would be that of the plate, but the primarySample is the plate well at
  // plateWellPosition.
  PLATE_POSITION_DUPLE = 'PLATE_POSITION_DUPLE',
}

export interface StructuredTableDomainGraphType {
  columnGroup: ColumnGroup | null;
  objectSpec: EphemeralObjectSpec;
  columnType: TableColumnType;
}

/**
 * Base model for structured table row updaters. All row updater models must satisfy this.
 */
export interface StructuredTableRowUpdaterModel {
  id: string;
}

/**
 * A StructuredTableRowUpdater provides an extension point for how StructuredTables can handle fetching
 * live values for PrimarySamples and updating rows when refreshing the table. An example of the
 * power that this gives table implementors is how we support ingredients for the Mixture Ingredients table.
 * Using a custom row updater, we can define a GraphQL query which returns the ingredient information for the
 * row and then update the amount or catalog columns in the row with that information.  This updater is
 * particular to the Mixture Ingredients table and does not affect any other table types.
 *
 * The `updateRow` method is called when refreshing a table.  It's handed the updated model along with the
 * list of columns for the current table, as well as the existing row.  It should return a new row with any
 * updated information.
 *
 * The `fetchModel` method is called when attempting to update a sample for the row or when checking the
 * current value of the sample.
 */
export interface StructuredTableRowUpdater<
  TModel extends StructuredTableRowUpdaterModel = StructuredTableRowUpdaterModel,
  TRowMetadata = unknown,
> {
  /**
   * The updateRow function is called with the updated model, the columns in the table, the current row
   * and the columnGroup that the model should be associated with.  The columnGroup is determined by the
   * columnGroup setting on the PrimarySample which is returned in the
   * StructuredTableAdapter.getPrimarySamples.  It's needed when there might be multiple samples of the same
   * type that are contained in the same row.
   */
  readonly updateRow: (
    model: TModel | InaccessibleInfo,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<TRowMetadata>,
    columnGroup: ColumnGroup | null
  ) => StructuredTableRow<TRowMetadata>;

  /**
   * The updateCell function is called with the updated model, and columnGroup that the model is
   * associated with (if one exists),  the column in the table to be updated.  Of the samples returned by
   * StructuredTableAdapter.getPrimarySamples, the columnGroup should match whatever primary sample this
   * model corresponds to. It's needed when there might be multiple samples of the same type that are contained
   * in the same row.
   */
  readonly updateCell: (
    model: TModel | InaccessibleInfo,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ) => LiveCellValue;

  /**
   * Should always behave identically to bulkFetchModel but with just one input supplied. It will return the first
   * corresponding result of the bulk query. Whenever you need multiple results simultaneously, prefer using bulkFetchModel.
   */
  readonly fetchModel: (sampleLookupInfo: SampleLookupInfo) => Promise<TModel | InaccessibleInfo>;

  /**
   * When bulkFetchModel is called, an async call should be made to retrieve the models from the server.
   * This value may then be passed into the `updateRow` method to update the row to the most recent version
   * from the server or we might rely on the value being available from the `readFromCache` in a different
   * event loop to mark the cell as being stale.
   */
  readonly bulkFetchModel?: (
    sampleLookupInfo: Array<SampleLookupInfo>
  ) => Promise<Array<TModel | InaccessibleInfo>>;

  /**
   * When we are checking if the values in the current row are stale, we want to make a synchronous call to
   * the cache to find out if there is an updated value.  This method should return the most recent value in
   * the cache or null if it's not available.  Additionally, if the value in the cache is an
   * InaccessibleInfo, this method should return null.
   */
  readonly readFromCache: (sampleLookupInfo: SampleLookupInfo) => TModel | null;
}

export interface StructuredTableCustomComponentSlotProps<TTableMetadata, TRowMetadata> {
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  computedTable: EvaluatedTable;
  /** ⚠️ CHEMISTRY_TABLE: A direct handler to edit the StructuredTable. This updateHandler will likely be
   * removed in the future because it can miss important side-effects of editing tables such as formula
   * shifting. This will be replaced with a proper TableOp for updating cells.
   */
  updateHandler: (structuredTable: StructuredTable<TTableMetadata, TRowMetadata>) => void;
}

/**
 * A StructuredTableAdapter provides the necessary glue for converting
 * a StructuredTable into a Table which can be displayed by
 * TableEditable.  It consists of transformation functions from and to
 * the Table, as well as a method for converting a set of CreateStructuredTableProps
 * into a StructuredTable.  Additionally, a kind is provided which is
 * used to discriminate which kind of StructuredTable this
 * StructuredTableAdapter is valid for.
 *
 * A StructuredTableAdapter takes in two type parameters:
 * - TTableMetadata - the type of the metadata which is stored on the table
 * - TRowMetadata - the type of the metadata which is stored on the row
 *
 * If you implement this interface without inheriting from GenericStructuredTableAdapter, it is recommended
 * that your implementation respect ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS.
 */
export interface StructuredTableAdapter<TTableMetadata, TRowMetadata> {
  /**
   * Return a new StructuredTable derived from the given
   * `createProps`.  The StructuredTable that's returned does not have
   * a cid property yet as that's up to the note-editor to insert.
   *
   * @param createProps attributes to be used in determining the StructuredTable structure
   */
  createTable(
    createProps: CreateStructuredTableProps<TTableMetadata>
  ): Omit<StructuredTable<TTableMetadata, TRowMetadata>, 'cid'>;

  /**
   * Given the column and row that we are currently in, returns true if we consider the cell as being
   * read-only (i.e. locked) for this particular table type.
   *
   * @param currentColumnIndex - the current column index
   * @param row - the current row
   * @param structuredTable - the structured table that we're working on
   */
  isPositionReadOnly(
    currentColumnIndex: number,
    row: StructuredTableRow<TRowMetadata>,
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
  ): boolean;

  /**
   * Returns whether the column can be modified (deleted) in the context of that structured table
   *
   * @param structuredTable - the structured table containing the columns
   * @param column - the StructuredTableColumn that we're evaluating editability of
   * @returns whether the structured table allows for the column to be modified
   */
  isColumnUserConfigurable(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    column: StructuredTableColumn | null
  ): boolean;

  /**
   * Convert a StructuredTable into a Table which can be displayed in TableEditable
   * @param structuredTable the StructuredTable to convert
   */
  toTable(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): Table;

  /**
   * Convert a TableEditable Table into a StructuredTable
   * @param table the Table to convert
   */
  fromTable(table: TableData): StructuredTable<TTableMetadata, TRowMetadata>;

  /**
   * Returns all the columns supported by this structured table
   * @param structuredTableInsertionContext This context is currently only passed in by a consumer in specific cases
   * where table functionality depends on it. In all other cases, an empty default is provided.
   * If you need to depend on this context, please verify the DOM tree containing the table has a
   * StructuredTableInsertionContext consumer. See {@link StructuredTableInsertionContext} for more details.
   */
  getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string | null;
    metadata: TTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn>;

  /**
   * For each schema, indicate what {@link ObjectType}s are supported.
   *
   * This can be used to determine what platform-capability-linked columns should be available to a table.
   */
  getDomainGraphTypes(
    schemas: Array<SchemaAndColumnGroup>,
    metadata: TTableMetadata
  ): Array<StructuredTableDomainGraphType>;

  /**
   * Implementations can optionally inject their frontEndMetadata into a column here. No
   * need to invoke super, which is a noop.
   */
  injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: TTableMetadata;
  }): StructuredTableColumn;

  /**
   * Given a StructuredTable, return a blank row.
   * @param tableMetadata - the TTableMetadata for this adapter
   * @param rowIndex - the index of the row that we're creating
   * @param columns - an array of all the columns that should be present in this row
   * @param initialRowMetadata - optional specification of initial metadata to set on this row
   */
  getBlankRow(
    tableMetadata: TTableMetadata,
    rowIndex: number,
    columns: Array<StructuredTableColumn>,
    initialRowMetadata?: TRowMetadata
  ): StructuredTableRow<TRowMetadata>;

  /**
   * If we're given a Table's array of cells and its RowData, as well
   * as our TTableMetadata, we need a way to get back our
   * StructuredTableRow with our TRowMetadata as well.  Since
   * TRowMetadata is specific to each adapter type, we need to
   * implement this per adapter.
   *
   * Note: the cells and rowData here may contain UI properties.  If it's not
   * desired to persist those values, then they should be omitted in
   * the implementation of this method.
   *
   * @param cells - the array of Cells from our TableData for this row
   * @param metadata - the metadata for this particular table
   * @param rowData - this also comes from our TableData for this row
   */
  toTableRow(
    cells: Array<Cell>,
    metadata: TTableMetadata,
    rowData: RowData
  ): StructuredTableRow<TRowMetadata>;

  /**
   * A StructuredTableRowUpdater which should be used for this table implementation. These are
   * used when refreshing the table or checking for stale values.
   *
   * This row updater should focus on kind-specific columns and any columns that are not handled by
   * ./StructuredTableColumnAdapters.ts.
   *
   * Callers should generally use `getAllRowUpdaters()` instead of calling this method directly.
   */
  getTableKindRowUpdater(): StructuredTableRowUpdater<StructuredTableRowUpdaterModel, TRowMetadata> | null;

  /**
   * A list of StructuredTableRowUpdaters that together can combine all columns of a structured table.
   * @param columns - the list of columns that are in this table to filter the row updaters by based on type.
   * Returns all enabled row updaters if columns param is undefined
   */
  getAllRowUpdaters<TIds>(
    columns?: Array<GenericStructuredTableColumn<TIds>>
  ): Array<StructuredTableRowUpdater>;

  /**
   * A token which identifies which kinds of StructuredTable this class should be used for
   */
  kind: StructuredTableKind;

  /**
   * The name of the table type which is used in informational messages and in the label of the table.
   */
  getDisplayName: (structuredTable: StructuredTable<TTableMetadata, TRowMetadata>) => string;

  /**
   * The name of the table row type which is used in informational messages
   * and in the label of the table rows or table forms.
   */
  getRowDisplayName?: (structuredTable: StructuredTable<TTableMetadata, TRowMetadata>) => string;

  /**
   * Most tables display as tables. However, in certain instances, a one-row table needs to be
   * displayed as an interactive form. Adapters can made this determination as needed.
   */
  shouldShowAsForm(table: StructuredTable<TTableMetadata, TRowMetadata>): boolean;

  /**
   * When rendered in form view, an adapter can modify how the form fields are laid out.
   *
   * @param columns - the list of columns that each typedValueEditor corresponds to
   * @param formFields - the list of form fields that are being laid out
   */
  renderFormContents(columns: Array<ColumnDefinition>, formFields: Array<JSX.Element>): JSX.Element | null;

  /**
   * When rendered in form view, an adapter can modify the layout of the form fields.
   */
  renderFormField(editor: JSX.Element, columnDefinition: ColumnDefinition, columnIndex: number): JSX.Element;

  /**
   * If an adapter responds TRUE to this query, the entire title bar will be hidden. This also suppresses
   * the formula box, as well as any action functions the table wants to provide. And the user will have
   * no way to change the table name. So use with care!
   */
  shouldSuppressTitleBar(table: StructuredTable<TTableMetadata, TRowMetadata>): boolean;

  /**
   * The ActionFunctions to make available on the table.
   */
  getActionFunctions(
    tableMetadata: TTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<TTableMetadata, TRowMetadata>;

  /**
   * A list of renderers to put information into the right side of the footer.
   */
  getFooterInformation(): Array<FooterInfoRenderer<TTableMetadata, TRowMetadata>>;

  /**
   * The list of ways new, filled rows can be added to the table from sources outside the table (e.g.,
   * another table, a worklist, etc). These are rendered as a menu behind a [+] button
   */
  getAddFromSourceOperations(
    entryId: string,
    structuredTable?: StructuredTable<TTableMetadata, TRowMetadata>,
    addAIDataEntryRows?: (
      initialPromptText?: string,
      initialSelectedTableName?: string,
      result?: AddStructuredTableRowsUsingAIResult | null
    ) => void
  ): Array<StructuredTableAddOperation<TTableMetadata, TRowMetadata>>;

  getColumnDefinitions(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
  ): Array<ColumnDefinition>;

  /**
   * Returns a view of the data contained in the table organized around the primary samples of the
   * table.  This can then be used to compute the StoreDependencyTable and also to help determine
   * what the stale values are of each row.
   *
   * The return of this method organizes itself around the rowId of each row.  Each row has a UUID
   * that can be used to uniquely identify it and allows us to match that row to the primary sample.
   *
   * We include in the call the computedTable representation of the table so we can properly handle
   * cells which contain formulas.  However, that parameter is optional as there are cases where we
   * might not have a computedTable yet and we just want to extract the samples from the persisted
   * structured table model, i.e. making the spreadsheet store dependency map in the
   * STRUCTURED_TABLE_PLUGIN.
   */
  getPrimarySamples(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    computedTable?: Array<Array<EvaluatedCell>> | null,
    structuredTableRowsBatch?: Array<StructuredTableRow<TRowMetadata>>
  ): {[rowId: string]: Array<PrimarySample>};

  /**
   * Returns true if the unsubmitted changes indicators should be skipped for the given row.
   */
  skipUnsubmittedIndicatorsForRow(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    row: StructuredTableRow<TRowMetadata>
  ): boolean;

  /**
   * Returns a list of the boolean values on whether the row at index i is unsubmitted.
   */
  getRowUnsubmittedStatuses(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    computedTable: Array<Array<EvaluatedCell>>
  ): Array<boolean>;

  /**
   * Returns a 2d list of the boolean values on whether the cell at index i,j is unsubmitted.
   */
  getCellUnsubmittedStatuses(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    computedTable: Array<Array<EvaluatedCell>>
  ): Array<Array<boolean>>;

  /**
   * Returns a list of Excess formulas which will get passed along to the customEval function for
   * this type of table.  This gets evaluated inside of the Excess Evaluator, so returning a
   * formula relative to the current column is a fantastic way to get the evaluated value of a
   * different cell within this row.
   *
   * An example for when this might be useful is when you want to base one column onto the value of
   * another column, e.g. locking all cells except for one column until that columns has text.
   *
   * In that example, we know that we have an EXISTING_BIOENTITY column in our table and we want to
   * make the BIOENTITY_NAME column locked until we have a value in the EXISTING_BIOENTITY column.
   * To do this, we need the Excess Evaluator to give us the typedValue that's in the
   * EXISTING_BIOENTITY value when we are evaluating the BIOENTITY_NAME column's customEval function.
   *
   * With our getCustomEvalArgs function, we can find the column index of the EXISTING_BIOENTITY column
   * and use the provided currentRowIndex to create a formula that references the spreadsheet index
   * of the EXISTING_BIOENTITY column of our current row.  Excess will then look that value up and
   * give it to our customEval function as one of our cell dependencies.  We can then check if that value
   * is empty and, if so, return a locked cell.
   *
   * If we don't have any customEval dependencies that we want, we can just return an empty array.
   *
   * @param structuredTable: the structured table object
   * @param cellIndex: the index of the cell we want custom eval args for
   * @param options.toSpreadsheetIndex an function to map a row/col index to a target cell, useful for merged ranges
   */
  getCustomEvalArgs(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    cellIndex: CellIndex,
    options: {
      toSpreadsheetIndex: (cellId: CellIndex) => string;
    }
  ): Array<string>;

  /**
   * Returns a CustomEval function or null if there is no custom eval function which is
   * appropriate for this type of column in this table.  The customEval function allows us to inject
   * custom logic into the Excess Evalutor to do things like override the cell's value based upon other
   * values in the row or to do additional validation on the cell's contents.  For instance, the
   * RegistrationStructuredTableAdapter provides an implementation of this function which does additional
   * validation on the EXISTING_BIOENTITY column but only if the Registration table's metadata indicates
   * that it's a REGISTER_EXISTING_BIOENTITY type.
   *
   * The returned CustomEval function gets run against each cell in the table.
   *
   * If there is no customEval functionality needed for the table, this should just return null.
   *
   * @param structuredTable: the structured table object
   * @param cellIndex: the index of the cell we want a custom eval for
   */
  getCustomEvalFn(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{tableMetadata: TTableMetadata; rowMetadata: TRowMetadata}> | null;

  /**
   * @deprecated Returns a list of sample infos from the table that can be used for creating requests.
   *
   * Note that new usages should be avoided, and getPrimarySamples should be used instead when possible.
   */
  getSampleInfosForRequest(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
  ): Array<SampleLookupInfoForCreateRequest>;

  /**
   * Return a list of TableValidationFn's that should be run for any tables
   * which are of this StructuredTableKind.
   */
  getTableValidationFns(): Array<TableValidationFn<TTableMetadata, TRowMetadata>>;

  /**
   * Modifies the appearance of the structuredTable to be used as preview in insertion modals
   */
  prepareForPreview(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
  ): StructuredTable<TTableMetadata, TRowMetadata>;

  /**
   * Returns a summary of the primary column used for creating processes.
   *
   * The primary column serves as the default source column for this table when propagating data from
   * this table to a destination table in the process creation modal. This method allows your table to
   * indicate which destination columns in the task table the primary column can map to.
   */
  getColumnSummariesForCreateProcessShortcut(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
  ): Array<StructuredTableColumnSummary & {columnIndex: number}>;

  /**
   * Tables try to never consult live data when rendering. This allows us to maintain an
   * accurate version history for compliance, etc.
   *
   * However, when live data changes -- e.g., the user changes the name of an entity
   * in the registry -- we want to do two things:
   * (1) continue to show the old name in the table
   * (2) let the user know that the live value doesn't match the rendered value (a "stale indicator").
   *
   * This method allows your table to indicate which live data are relevant for showing
   * stale indicators. The live data is extracted for staleness checks in `LiveCellValues.ts`.
   *
   * Note: This function should only be defined for tables that don't have a fully functional
   * StructuredTableRowUpdater (e.g. if there are live values that for some reason cannot be fetched and
   * cached in Apollo, and it's necessary to instead read from the Redux store).
   */
  getStoreDataForLiveValues(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): {
    fileSummariesById?: {[bioentityId: string]: BioentitySummary};
    storableSummariesById?: {[storableId: string]: StorableSummary};
  };

  /**
   * Validate the length of a structured table after adding rows.
   * @param structuredTable The structured table being added to.
   * @param newTableLength The length of the new table, after modifying and adding rows.
   * @param tableIsEditable - Whether the table is fundamentally editable or not. Tables in Runs or the Results pane of bioentities,
                                                 for example, are never directly editable, and use a different config flag to determine their max number
                                                 of rows. Tables that are editable (param is `True`) use the ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS flag,
                                                 and tables that are read-only (param is `False`) use ELN_MAX_STRUCTURED_TABLE_ROWS.
   * @param numPendingInAdded The number of rows being added that are accounted for in the table's count of pending rows, since we reserve space for pending rows when checking the max rows config.
   * @returns An error message string if applicable. If there are no errors, returns null.
   */
  getTableLengthErrorMessage(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    newTableLength: number,
    isTableEditable?: boolean,
    numPendingInAdded?: number
  ): string | null;

  /**
   * Get the max number of rows that can be added to the table. This number is based on config flags, with
   * any count of pending rows subtracted, to reserve space for them to be integrated into the table later
   * without exceeding the max specified by the config flags.
   * @param structuredTable - The table that you want to evaluate for capacity for new rows. If not specified, pending rows will not be accounted for.
   * @param tableIsEditable - Whether the table is fundamentally editable or not. Tables in Runs or the Results pane of bioentities,
                                                 for example, are never directly editable, and use a different config flag to determine their max number
                                                 of rows. Tables that are editable (param is `True`) use the ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS flag,
                                                 and tables that are read-only (param is `False`) use ELN_MAX_STRUCTURED_TABLE_ROWS.
   * @returns The maximum number of rows that can be added to the given table.
   */
  getTableLengthCapacity(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    tableIsEditable?: boolean
  ): number;

  /**
   * Constructs an informative string about the given tables capacity for new rows. Useful for when a warning or error needs to be shown
   * about preventing a table from exceeding this capacity, since it includes the count of both present and pending rows, if there are
   * any.
   * @param structuredTable - The table you want to evaluate for capacity for new rows. If not specified, pending rows will not be accounted for.
   * @param tableIsEditable - Whether the table is fundamentally editable or not. Tables in Runs or the Results pane of bioentities, for example, are never directly editable, and use a different config flag to determine their max number of rows. Tables that are editable (param is `True`) use the ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS flag, and tables that are read-only (param is `False`) use ELN_MAX_STRUCTURED_TABLE_ROWS.
   * @returns A string reflecting the max number of rows the given table can hold, plus a count of current rows, and pending rows that space is being reserved for, if there are any. The string returned will either say "# rows max", or "# rows max; # present, # pending", with the present and pending counts being excluded if they are 0 or not available (e.g. no structured table is given).
   *
   * Examples: "500 rows max", or "200 rows max; 195 present, 24 pending", or "200 rows max; 200 present"
   */
  getTableLengthString(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    tableIsEditable?: boolean
  ): string;

  areRequiredSchemasArchived(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): boolean;

  /**
   * Returns whether the the structured table should be read-only.
   */
  isReadOnly(): boolean;

  /**
   * Returns whether or not we allow pasting this kind of structured table.
   *
   * Note that there are already some conditional reasons that we disallow paste captured in
   * StructuredTablePlugin.itemFromHTML.
   */
  allowPasteTable(): boolean;

  /**
   * Returns whether or not we allow deleting this kind of structured table.
   *
   * Note that there may be conditional reasons that delete is disallowed even if this returns true.
   *
   * @param structuredTable the StructuredTable to see if we can delete it
   * @returns an object with canDelete set to true if it can be deleted and a reason that can be shown
   */
  allowDeleteTable(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): {
    canDelete: boolean;
    reason?: string;
  };

  /**
   * Returns whether or not we allow reordering rows and columns in this kind of structured table.
   *
   * Note that there may be conditional reasons that reordering is disallowed even if this returns true.
   *
   * @param structuredTable the StructuredTable to see if we can reorder it
   * @returns an object with row and column set to true if they can be reordered
   */
  allowReorderDimensions(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): TableReorderLocks;

  /**
   * Returns whether or not the table allows cells in the specified column to be merged.
   *
   * @param structuredTable The table containing the column.
   * @param column The column containing the cells to be merged.
   * @returns true iff the merge should be allowed.
   */
  allowMergedCellRange(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    column: StructuredTableColumn
  ): boolean;

  /** Returns whether or not the table allows Columns to be transformed to/from Single Inputs */
  allowSingleInputs(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): boolean;

  /** Return the icon that should identify the table. */
  getTableIcon(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): BenchlingIconName;

  /**
   * For a given row, get the columns that particular row has in order. Notably, this replaces ROW_DELEGATE
   * columns with their delegated columns.
   */
  getColumnsForRow(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    row: StructuredTableRow<TRowMetadata>
  ): Array<StructuredTableColumn>;

  /** When true, allows this table to be a destination for Table Mapping */
  allowAsTableMappingDestination(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): boolean;

  /**
   * When true, allows this table to have custom columns.
   *
   * To enable custom columns on your table, you must override this function in the adapter, and add the output of
   * and ensure that the custom column value is added to the "lastSyncedUpstreamValues" property of each row
   * upon table submission.
   * See https://github.com/benchling/aurelia/pull/63688 for an example of how to do so.
   *
   * The list of allowed tables types intentionally does NOT include any of the tables
   * in a Lab Automation run component, which are housed in `RunOutputSamplesView` and `ResultsTableView`,
   * not `StructuredTableView`.
   */
  enableCustomColumns(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): boolean;

  /**
   * When true, allows this table to have lookup columns
   *
   * The list of allowed tables types intentionally does NOT include any of the tables
   * in a Lab Automation run component, which are housed in `RunOutputSamplesView` and `ResultsTableView`,
   * not `StructuredTableView`.
   */
  enableLookupColumns(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): boolean;

  /**
   * ⚠️ CHEMISTRY_TABLE: This is an experimental interface that will likely change in the future. This is needed for
   * the first iteration of Chemistry Tables.
   *
   * A customizable slot that sits under the title bar and above the table. Can be used to render any custom
   * components within the table container.
   *
   * This is purposely flexible, which can quickly become dangerous if not used carefully:
   * - Any state in the custom component that you wish to be persisted should be stored in the StructuredTable (either in the cells or metadata). This ensures that the state is versioned with the table.
   * - This component will re-render whenever these props are updated. Expensive components should be memoized.
   * - Be cautious of vertical screen real-estate, consider max heights
   * - Adhere to the existing table styles
   */
  experimental_renderAboveTableCustomComponent(
    props: StructuredTableCustomComponentSlotProps<TTableMetadata, TRowMetadata>
  ): React.ReactNode;
}

export interface GetCustomEvalArgsOptions {
  toSpreadsheetIndex: (cellId: CellIndex) => string;
}

/**
 * Represents a result from a TableValidationFn which lets the StructuredTable
 * framework know where an error should be placed. The general strategy is that
 * multiple table validation functions will be called and then the errors
 * returned can be merged into specific locations in the table. The rowIndex and
 * columnIndex are the locations within the table that we should annotate with
 * the EvaluatedCellError. If the error is null, then no error will be shown
 * unless a different TableValidationFn annotates that indication with an error.
 */
export interface CellErrorPosition {
  rowIndex: number;
  columnIndex: number;
  error: EvaluatedCellError | null;
}

export type TableValidationFn<TTableMetadata, TRowMetadata> = (
  table: StructuredTable<TTableMetadata, TRowMetadata>,
  computedTable: Array<Array<EvaluatedCell>>
) => Array<CellErrorPosition>;

/**
 * The action functions available on a given table grouped by UI location.
 */
export interface ActionFunctions<TTableMetadata, TRowMetadata> {
  primary?: Array<ToolbarButtonActionFunction<TTableMetadata, TRowMetadata>>;
  secondary?: Array<ToolbarButtonActionFunction<TTableMetadata, TRowMetadata>>;
  overflow?: Array<OverflowMenuActionFunction<TTableMetadata, TRowMetadata>>;
  contextMenu?: Array<RowContextMenuActionFunction<TTableMetadata, TRowMetadata>>;
  footer?: Array<ToolbarButtonActionFunction<TTableMetadata, TRowMetadata>>;
}

export type FooterInfoRenderer<TTableMetadata, TRowMetadata> = (
  params: GetFooterInformationParams<TTableMetadata, TRowMetadata>
) => JSX.Element | null;

/**
 * Subclasses of StructuredTableAdapter can pass a configuration object to the parent constructor.
 */
export type StructuredTableAdapterOptions = Partial<{
  allowMultipleRowsInConfigs: boolean;
  allowNoRowsInConfigs: boolean;
  allowNoRowsInEntries: boolean;
  locked: boolean;
}>;

export type UnknownStructuredTableAdapter = StructuredTableAdapter<unknown, unknown>;

/**
 * An enum of all the possible ColumnGroups that could be contained
 * in a StructuredTableColumn.
 *
 * Keep in sync with benchling/notebook/structured_tables/models/structured_table_column.py
 */
export enum StructuredTableColumnGroups {
  INGREDIENT_MEASURED_AMOUNT = 'INGREDIENT_MEASURED_AMOUNT',
  INGREDIENT_TARGET_AMOUNT = 'INGREDIENT_TARGET_AMOUNT',
  INGREDIENT_COMPONENT_LOT_STORAGE = 'INGREDIENT_COMPONENT_LOT_STORAGE',
  SAMPLE_OWNERS = 'SAMPLE_OWNERS',
  AUTHORIZED_USERS = 'AUTHORIZED_USERS',
  CONCENTRATION = 'CONCENTRATION',
  QUANTITY = 'QUANTITY',
  REFERENCE = 'REFERENCE',
  TARGET = 'TARGET',
  // Chemistry Column Roles
  CHEMISTRY_MOLECULE = 'CHEMISTRY_MOLECULE',
  CHEMISTRY_REACTION_ROLE = 'CHEMISTRY_REACTION_ROLE',
  CHEMISTRY_LIMITING_REACTANT = 'CHEMISTRY_LIMITING_REACTANT',
  CHEMISTRY_EQUIVALENCE = 'CHEMISTRY_EQUIVALENCE',
  CHEMISTRY_MOLECULAR_WEIGHT = 'CHEMISTRY_MOLECULAR_WEIGHT',
  CHEMISTRY_MOLECULE_PURITY = 'CHEMISTRY_MOLECULE_PURITY',
  CHEMISTRY_MOLES_PLAN = 'CHEMISTRY_MOLES_PLAN',
  CHEMISTRY_MOLES_ACTUAL = 'CHEMISTRY_MOLES_ACTUAL',
  CHEMISTRY_MOL_UNIT = 'CHEMISTRY_MOL_UNIT',
  CHEMISTRY_AMOUNT_PLAN = 'CHEMISTRY_AMOUNT_PLAN',
  CHEMISTRY_AMOUNT_ACTUAL = 'CHEMISTRY_AMOUNT_ACTUAL',
  CHEMISTRY_AMOUNT_UNIT = 'CHEMISTRY_AMOUNT_UNIT',
  CHEMISTRY_CHEMICAL_FORMULA = 'CHEMISTRY_CHEMICAL_FORMULA',
  CHEMISTRY_IUPAC_NAME = 'CHEMISTRY_IUPAC_NAME',
  CHEMISTRY_CAS_REGISTRY_NUMBER = 'CHEMISTRY_CAS_REGISTRY_NUMBER',
  CHEMISTRY_CONCENTRATION = 'CHEMISTRY_CONCENTRATION',
  CHEMISTRY_CONCENTRATION_UNIT = 'CHEMISTRY_CONCENTRATION_UNIT',
  CHEMISTRY_AMOUNT_YIELD = 'CHEMISTRY_AMOUNT_YIELD',

  // Non-persisted columns
  FILL_PLATE_SOURCE_CONCENTRATION = 'FILL_PLATE_SOURCE_CONCENTRATION',
  FILL_PLATE_TARGET_CONCENTRATION = 'FILL_PLATE_TARGET_CONCENTRATION',
  FILL_PLATE_TRANSFER_QUANTITY = 'FILL_PLATE_TRANSFER_QUANTITY',
  FILL_PLATE_FINAL_QUANTITY = 'FILL_PLATE_FINAL_QUANTITY',
}

export type SchemaAndColumnGroup = (Schema | BatchSchema | ProcessTaskSchema | SchemaWithDescription) & {
  columnGroup: ColumnGroup | null;
};

export interface BasicTableProps {
  icon: BenchlingIconName;
  indentation: number;
  // The header of the table is a combination of `tableTypeLabel` and `tableName`.
  // `tableTypeLabel` represents the type of the table, such as `Transfer`
  // and `tableName` would be the unique name of the table.
  tableTypeLabel?: string;
  tableName: string;
}

export interface ColumnWithPositionalIndex {
  column: StructuredTableColumn;
  columnIndex: number;
}

export const RETRIEVE_PRIMARY_SAMPLES_INTERVAL_MS = 60000 * 10; // 10 minutes
